import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';

const JsonEditor = ({ isOpen, onClose, formData, onApply }) => {
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [editorLoading, setEditorLoading] = useState(true);

  // Update JSON text when formData changes or modal opens
  useEffect(() => {
    if (isOpen && formData) {
      setJsonText(JSON.stringify(formData, null, 2));
      setError('');
      setIsValid(true);
    }
  }, [isOpen, formData]);

  // Apply JSON changes to form
  const handleApply = () => {
    if (!isValid) return;

    try {
      const parsedData = JSON.parse(jsonText);
      onApply(parsedData);
      onClose();
    } catch (err) {
      setError(`Error applying JSON: ${err.message}`);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      // Escape to close
      if (e.key === 'Escape') {
        onClose();
      }

      // Ctrl+S or Cmd+S to apply changes
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isValid) {
          handleApply();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isValid, onClose, jsonText]);

  // Validate JSON as user types
  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonText(value);

    try {
      if (value.trim()) {
        const parsed = JSON.parse(value);

        // Validate form data structure
        const validationError = validateFormData(parsed);
        if (validationError) {
          setError(`Form validation error: ${validationError}`);
          setIsValid(false);
        } else {
          setError('');
          setIsValid(true);
        }
      } else {
        setError('JSON cannot be empty');
        setIsValid(false);
      }
    } catch (err) {
      setError(`Invalid JSON: ${err.message}`);
      setIsValid(false);
    }
  };

  // Validate form data structure
  const validateFormData = (data) => {
    if (!data || typeof data !== 'object') {
      return 'Form data must be an object';
    }

    // Check required top-level fields
    const requiredFields = ['modeling_type', 'model', 'normalize'];
    for (const field of requiredFields) {
      if (!(field in data)) {
        return `Missing required field: ${field}`;
      }
    }

    // Validate modeling_type structure
    if (!data.modeling_type || typeof data.modeling_type !== 'object') {
      return 'modeling_type must be an object';
    }

    const { modeling_type } = data;
    if (!modeling_type.name || !['classification', 'regression'].includes(modeling_type.name)) {
      return 'modeling_type.name must be "classification" or "regression"';
    }

    if (modeling_type.name === 'classification' && !modeling_type.type) {
      return 'Classification requires modeling_type.type';
    }

    if (modeling_type.name === 'classification' && modeling_type.type && !['binary', 'multi-class'].includes(modeling_type.type)) {
      return 'Classification type must be "binary" or "multi-class"';
    }

    // Validate arrays if they exist
    if (data.filter_data && !Array.isArray(data.filter_data)) {
      return 'filter_data must be an array';
    }

    // Validate evaluation_metrics structure
    if (data.evaluation_metrics) {
      if (typeof data.evaluation_metrics !== 'object') {
        return 'evaluation_metrics must be an object';
      }

      if (data.evaluation_metrics.evaluation_metrics_list && !Array.isArray(data.evaluation_metrics.evaluation_metrics_list)) {
        return 'evaluation_metrics.evaluation_metrics_list must be an array';
      }

      if (data.evaluation_metrics.top_n !== undefined && (typeof data.evaluation_metrics.top_n !== 'number' || data.evaluation_metrics.top_n < 1)) {
        return 'evaluation_metrics.top_n must be a positive number';
      }
    }

    return null; // No validation errors
  };

  // Format JSON with proper indentation
  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setError('');
      setIsValid(true);
    } catch (err) {
      setError(`Cannot format invalid JSON: ${err.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-2 border-b">
          <h2 className="text-lg font-medium text-gray-900">JSON Editor</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            title="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 overflow-hidden flex flex-col">
          {/* Toolbar */}
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={handleFormat}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              Format
            </button>
            <div className="text-xs text-gray-500">
              {jsonText.split('\n').length} lines, {jsonText.length} chars
            </div>
          </div>

          {/* JSON Editor */}
          <div className="flex-1 flex flex-col">
            <div className={`flex-1 border rounded-lg overflow-hidden ${
              isValid ? 'border-gray-300' : 'border-red-300'
            }`}>
              <Editor
                height="100%"
                defaultLanguage="json"
                value={jsonText}
                onChange={(value) => handleJsonChange({ target: { value: value || '' } })}
                onMount={() => setEditorLoading(false)}
                theme="vs"
                loading={
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500">Loading editor...</div>
                  </div>
                }
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  lineNumbers: 'on',
                  roundedSelection: false,
                  scrollbar: {
                    vertical: 'visible',
                    horizontal: 'visible',
                    verticalScrollbarSize: 8,
                    horizontalScrollbarSize: 8
                  },
                  overviewRulerLanes: 0,
                  hideCursorInOverviewRuler: true,
                  bracketPairColorization: { enabled: true },
                  folding: true,
                  foldingHighlight: true,
                  wordWrap: 'on',
                  automaticLayout: true,
                  padding: { top: 10, bottom: 10 },
                  suggest: { showKeywords: false },
                  quickSuggestions: false,
                  parameterHints: { enabled: false }
                }}
              />
            </div>

            {/* Error Display */}
            {error && (
              <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                {error}
              </div>
            )}

            {/* Success indicator */}
            {isValid && jsonText.trim() && !error && (
              <div className="mt-1 flex items-center text-green-600 text-xs">
                <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Valid JSON
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-3 py-2 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            <span className="hidden sm:inline">ESC to close • </span>
            <span className="hidden sm:inline">⌘S to apply</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!isValid}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                isValid
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonEditor;
